import { fade, makeStyles, Theme } from '@material-ui/core/styles';

export const useStyles = makeStyles<Theme>(theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    paddingBottom: theme.spacing(2),
  },
  formHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  fieldLabel: {
    marginBottom: theme.spacing(0.5),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
  },
  questionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  card: {
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.common.gray300}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.common.white,
  },
  cardError: {
    borderColor: theme.palette.error.main,
  },
  dragHandle: {
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: theme.spacing(0.5),
    color: theme.palette.common.gray500,
    cursor: 'grab',
  },
  cardBody: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  controlsRight: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  questionInput: {
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
    padding: theme.spacing(0.5, 1),
    // Pull the borderless field back to the card's text edge.
    marginLeft: theme.spacing(-1),
    borderRadius: theme.shape.borderRadius,
    '&:hover, &.Mui-focused': {
      backgroundColor: theme.palette.common.gray100,
    },
  },
  questionInputError: {
    backgroundColor: fade(theme.palette.error.main, 0.08),
  },
  hintInput: {
    fontSize: theme.typography.body2.fontSize,
    color: theme.palette.common.gray600,
    padding: theme.spacing(1),
    marginLeft: theme.spacing(-1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.common.gray100,
    '& input::placeholder': {
      fontStyle: 'italic',
    },
  },
  addHint: {
    alignSelf: 'flex-start',
  },
  typeSelect: {
    minWidth: 180,
  },
  keyButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 1),
    border: 'none',
    borderRadius: theme.shape.borderRadius,
    background: 'none',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: theme.typography.body2.fontSize,
    '&:hover': {
      backgroundColor: theme.palette.common.gray100,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  keyIcon: {
    color: theme.palette.common.gray500,
  },
  keyPencil: {
    color: theme.palette.common.gray500,
  },
  standardChip: {
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: fade(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  iconControl: {
    color: theme.palette.common.gray500,
  },
  iconControlOn: {
    color: theme.palette.primary.main,
  },
  // Dense controls keep their hit areas when glyphs use Ripple's discrete size steps.
  restrictedButton: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    padding: theme.spacing(0.25),
  },
  removeButton: {
    width: theme.spacing(3.75),
    height: theme.spacing(3.75),
  },
  keyInput: {
    width: 320,
    maxWidth: '100%',
  },
  keyMono: {
    fontFamily: 'monospace',
    paddingBottom: 2,
    color: theme.palette.text.primary,
  },
  keyError: {
    borderColor: theme.palette.error.main,
  },
  visuallyHidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  undoBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  customKeyRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  picker: {
    width: 480,
    maxWidth: '90vw',
    display: 'flex',
    flexDirection: 'column',
  },
  pickerHeader: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.common.gray300}`,
  },
  pickerSearch: {
    padding: theme.spacing(1, 2),
  },
  pickerList: {
    maxHeight: 360,
    overflowY: 'auto',
    padding: 0,
  },
  pickerGroupLabel: {
    padding: theme.spacing(1.5, 2, 0.5),
    color: theme.palette.common.gray600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  pickerItem: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1, 2),
  },
  pickerItemKey: {
    fontFamily: 'monospace',
    color: theme.palette.common.gray600,
  },
  pickerFooter: {
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.common.gray300}`,
  },
  pickerEmpty: {
    padding: theme.spacing(3, 2),
    textAlign: 'center',
  },
  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  optionInput: {
    flex: 1,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  chip: {
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.common.gray100,
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    border: `1px dashed ${theme.palette.common.gray300}`,
    borderRadius: theme.shape.borderRadius,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  helperError: {
    color: theme.palette.error.main,
  },
  skeletonCard: {
    height: 152,
    borderRadius: theme.shape.borderRadius,
  },
}));
